/**
 * Copyright 2016-2021 Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * Custom search providers for the Fischenthal viewer.
 *
 * As of QWC2 2026, custom search providers are registered via
 * `registerSearchProvider(key, config)` from `qwc2/utils/SearchProviders`.
 * The provider `onSearch` signature is `(text, searchParams, callback)` and
 * results are delivered by calling `callback({results})`, where each result
 * group has the shape `{id, title|titlemsgid, type, items: [...]}`.
 *
 * The built-in `coordinates` and `nominatim` providers now ship with QWC2,
 * so only the Swisstopo `geoadmin` provider is defined here. The theme
 * `searchProviders` arrays in themesConfig.json reference these keys.
 */

import axios from 'axios';
import {SearchResultType, registerSearchProvider} from 'qwc2/utils/SearchProviders';
import CoordinatesUtils from 'qwc2/utils/CoordinatesUtils';

/** ****************************** geo.admin.ch ***************************** **/

function parseItemBBox(bboxstr) {
    if (bboxstr === undefined) {
        return null;
    }
    const matches = bboxstr.match(/^BOX\s*\(\s*(\d+\.?\d*)\s*(\d+\.?\d*)\s*,\s*(\d+\.?\d*)\s*(\d+\.?\d*)\s*\)$/);
    if (matches && matches.length < 5) {
        return null;
    }
    const xmin = parseFloat(matches[1]);
    const ymin = parseFloat(matches[2]);
    const xmax = parseFloat(matches[3]);
    const ymax = parseFloat(matches[4]);
    return CoordinatesUtils.reprojectBbox([xmin, ymin, xmax, ymax], "EPSG:21781", "EPSG:4326");
}

function geoAdminLocationSearchResults(obj) {
    const categoryMap = {
        gg25: "Municipalities",
        kantone: "Cantons",
        district: "Districts",
        sn25: "Places",
        zipcode: "Zip Codes",
        address: "Address",
        gazetteer: "General place name directory"
    };
    const resultGroups = {};
    (obj.results || []).map(entry => {
        if (resultGroups[entry.attrs.origin] === undefined) {
            resultGroups[entry.attrs.origin] = {
                id: entry.attrs.origin,
                title: categoryMap[entry.attrs.origin] || entry.attrs.origin,
                type: SearchResultType.PLACE,
                items: []
            };
        }
        const x = entry.attrs.lon;
        const y = entry.attrs.lat;
        resultGroups[entry.attrs.origin].items.push({
            id: entry.id,
            text: entry.attrs.label,
            x: x,
            y: y,
            crs: "EPSG:4326",
            bbox: parseItemBBox(entry.attrs.geom_st_box2d) || [x, y, x, y],
            provider: "geoadmin"
        });
    });
    return Object.values(resultGroups);
}

function geoAdminLocationSearch(text, searchParams, callback) {
    axios.get("https://api3.geo.admin.ch/rest/services/api/SearchServer?searchText=" + encodeURIComponent(text) + "&type=locations&limit=20")
        .then(response => callback({results: geoAdminLocationSearchResults(response.data)}))
        .catch(() => callback({results: []}));
}

/** ************************************************************************ **/

registerSearchProvider("geoadmin", {
    label: "Swisstopo",
    onSearch: geoAdminLocationSearch
});
